import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Sparkles, Send, Mail, ExternalLink, Info } from 'lucide-react';
import { calendarReps, enquiryEmailDraft, enquiryEmailSend } from '../lib/marketingApi';
import { formatApiError } from '../lib/auth';

const TIERS = [
  { value: 'first', label: 'First engagement', hint: 'Warm first reply — nudges a call based on the enquiry' },
  { value: 'second', label: 'Second engagement', hint: 'Gentle follow-up when they haven’t replied' },
  { value: 'followup', label: 'Follow-up assistance', hint: 'Supportive help based on the conversation so far' },
];

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); } catch (e) { return iso; }
}

// AI-drafted contextual reply, sent from the rep's own connected Microsoft 365 mailbox.
export default function EnquiryReply({ lead, canEdit, onSent }) {
  const emailLog = useMemo(() => lead.email_log || [], [lead.email_log]);
  const defaultTier = emailLog.length === 0 ? 'first' : 'second';

  const [reps, setReps] = useState([]);
  const [loadingReps, setLoadingReps] = useState(true);
  const [rep, setRep] = useState('');
  const [tier, setTier] = useState(defaultTier);
  const [instructions, setInstructions] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    calendarReps().then((d) => {
      setReps(d.reps || []);
      const me = (d.reps || []).find((r) => r.is_me);
      setRep((me || (d.reps || [])[0] || {}).crm_user_id || '');
    }).catch(() => {}).finally(() => setLoadingReps(false));
  }, []);

  const draft = async () => {
    setDrafting(true);
    try {
      const d = await enquiryEmailDraft(lead.id, { tier, instructions });
      setSubject(d.draft.subject || '');
      setBody(d.draft.body || '');
      toast.success('AI draft ready — review before sending');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Could not draft email')); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and message first'); return; }
    if (!rep) { toast.error('Connect a Microsoft 365 mailbox first'); return; }
    setSending(true);
    try {
      await enquiryEmailSend(lead.id, { rep_user_id: rep, subject, body, tier });
      toast.success('Email sent from your Microsoft 365 mailbox');
      setSubject(''); setBody(''); setInstructions('');
      onSent && onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, 'Could not send email')); }
    finally { setSending(false); }
  };

  const input = 'h-10 w-full rounded-md border border-slate-200 px-3 text-sm outline-none focus:border-[#0035A8]';

  if (loadingReps) return <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-300" /></div>;

  const hasRep = reps.length > 0;

  return (
    <div className="space-y-5" data-testid="enquiry-reply">
      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {hasRep && (
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Send from</label>
              <select value={rep} onChange={(e) => setRep(e.target.value)} data-testid="reply-rep-select" className={input}>
                {reps.map((r) => <option key={r.crm_user_id} value={r.crm_user_id}>{r.ms_email || r.name}{r.is_me ? ' (You)' : ''}</option>)}
              </select>
            </div>
          )}
          <div className={hasRep ? '' : 'sm:col-span-2'}>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Engagement stage</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="reply-tier-select" className={input}>
              {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <p className="mt-1.5 text-xs text-slate-500">{TIERS.find((t) => t.value === tier)?.hint}</p>

        <div className="mt-3">
          <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={2} data-testid="reply-instructions"
            placeholder="Optional: extra guidance for the AI (e.g. mention their 300-unit portfolio, keep it very short)…"
            className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#0035A8]" />
          <button onClick={draft} disabled={!canEdit || drafting} data-testid="reply-draft-btn"
            className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-md border border-[#0035A8] px-3.5 text-sm font-semibold text-[#0035A8] hover:bg-[#0035A8] hover:text-white disabled:opacity-60">
            {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft with AI
          </button>
        </div>

        <div className="mt-4 space-y-2 border-t border-slate-200 pt-4">
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reply-subject" placeholder="Subject" className={input} />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} data-testid="reply-body" placeholder="Write your reply, or use Draft with AI above…"
            className="w-full resize-y rounded-md border border-slate-200 px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#0035A8]" />
          {!hasRep ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="enquiry-reply-no-rep">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>You can draft here, but to <span className="font-semibold">send</span> from your own mailbox connect Microsoft 365 in <span className="font-semibold">Settings → My Calendar</span>.</span>
            </div>
          ) : canEdit ? (
            <button onClick={send} disabled={sending || !subject.trim() || !body.trim()} data-testid="reply-send-btn"
              className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-[#0035A8] text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send from my Microsoft 365
            </button>
          ) : <p className="text-xs text-slate-400">Read-only — ask an editor to send replies.</p>}
        </div>
      </div>

      {/* Sent history */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Mail className="h-3.5 w-3.5" /> Sent replies ({emailLog.length})</div>
        {emailLog.length === 0 ? (
          <p className="text-sm text-slate-400">No emails sent to this enquiry yet.</p>
        ) : (
          <div className="space-y-3" data-testid="reply-history">
            {[...emailLog].reverse().map((e) => (
              <div key={e.id} className="rounded-lg border border-slate-100 bg-white p-4">
                <div className="mb-1 flex items-center justify-between gap-2 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-2">
                    <span className="rounded bg-[#0035A8]/10 px-1.5 py-0.5 text-[10px] font-semibold capitalize text-[#0035A8]">{e.tier || 'reply'}</span>
                    <span className="font-medium text-slate-600">{e.sent_by}</span>
                    {e.sent_by_ms_email ? <span>· {e.sent_by_ms_email}</span> : null}
                  </span>
                  <span>{fmt(e.at)}</span>
                </div>
                <div className="text-sm font-semibold text-slate-800">{e.subject}</div>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600">{e.body}</p>
                {e.web_link ? <a href={e.web_link} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0035A8] hover:underline">View in Outlook <ExternalLink className="h-3 w-3" /></a> : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
